import { useRef } from 'react';
import {
  Badge,
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Separator,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@monksflow/monks-ui';
import { BookmarkIcon, EllipsisVerticalIcon } from 'lucide-react';
import { useGetDownloadUrl } from '@/shared/hooks/useGetDownloadUrl';
import { formatTimeAgo } from '@/shared/utils/dateUtils';
import { useBookmarkToggle } from '../../hooks';
import { displayRegion } from '../../model/region';
import { InsightTypeBadge } from './components/InsightTypeBadge';
import { AudienceTags } from './components/AudienceTags';
import { OpportunityTypeBadge } from '../OpportunityTypeBadge';
import type { OpportunitiesCardProps } from './OpportunitiesCard.types';
import { useTranslation } from 'react-i18next';

export function OpportunitiesCard({
  item,
  id,
  onClick,
  onToggleFavorite,
  isFavoriteLoading = false,
  isNew = false,
  onOpenDetail,
  bookmarkWording = 'radar',
  noBorder = false,
}: OpportunitiesCardProps) {
  const { t } = useTranslation('analysis');

  const menuInteractedRef = useRef(false);
  const { downloadUrl } = useGetDownloadUrl(item.thumbnail?.assetId);

  // Use the bookmark toggle hook
  const { isBookmarked, handleToggle: handleBookmarkClick } = useBookmarkToggle({
    itemId: item.opportunityId,
    isFavorite: item.isFavorite ?? false,
    onToggle: onToggleFavorite ?? (async () => {}),
  });

  // One key per wording rather than an interpolated noun: the preposition contracts with
  // the article in Spanish and Portuguese ("al radar" vs "a favoritos"), so the whole
  // phrase has to live in the locale.
  const bookmarkLabel = isBookmarked
    ? bookmarkWording === 'favorites'
      ? t('opportunity.opportunitiesCardV2.removeFromFavorites')
      : t('opportunity.opportunitiesCardV2.removeFromRadar')
    : bookmarkWording === 'favorites'
      ? t('opportunity.opportunitiesCardV2.addToFavorites')
      : t('opportunity.opportunitiesCardV2.addToRadar');

  const regionLabel = displayRegion(item.region);

  const audienceTagList = [
    ...new Set(item.targetAudienceProfiles?.map((profile) => profile.segmentName) ?? []),
  ];

  const handleCardClick = () => {
    if (menuInteractedRef.current) return;
    onClick(id);
    onOpenDetail?.(item.opportunityId);
  };

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={handleCardClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleCardClick();
        }
      }}
      id={id}
      data-testid="opportunities-card"
      className={`flex flex-col w-full transition-[border-color] duration-fast ease-out bg-card rounded-lg cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1 focus:ring-offset-card border ${noBorder ? 'border-transparent hover:border-border' : 'border-border-light hover:border-border-strong'}`}
    >
      {/* ── Header ─────────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between px-lg pt-lg">
        <div className="flex items-center gap-3 min-w-0">
          {isNew && (
            <span
              data-testid="opportunity-new-indicator"
              className="size-2 rounded-full bg-blue-600 shrink-0"
              aria-label={t('opportunity.opportunitiesCardV2.new')}
            />
          )}
          {item.durationType?.value && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span data-testid="opportunity-type-badge">
                    <OpportunityTypeBadge type={item.durationType.value} />
                  </span>
                </TooltipTrigger>
                <TooltipContent side="top">
                  {t('opportunity.opportunitiesCardV2.durationType')}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}
          <div className="flex items-center gap-2 min-w-0">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="text-xs text-muted-foreground whitespace-nowrap">
                    {formatTimeAgo(item.createdAt)}
                  </span>
                </TooltipTrigger>
                <TooltipContent side="top">
                  {t('opportunity.opportunitiesCardV2.dateOfGeneration')}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            {regionLabel && (
              <>
                {/* The component sizes a vertical rule with `h-full`, which resolves to
                    zero against this auto-height row — a variant-qualified height is
                    what overrides it, since a plain `h-*` loses to the variant. */}
                <Separator orientation="vertical" className="data-[orientation=vertical]:h-3" />
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span
                        data-testid="opportunity-market"
                        className="text-xs text-muted-foreground truncate w-full"
                      >
                        {regionLabel}
                      </span>
                    </TooltipTrigger>
                    <TooltipContent side="top">
                      {t('opportunity.opportunitiesCardV2.region')}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </>
            )}
          </div>
        </div>

        <div className="flex items-center shrink-0 ml-2">
          {/* No favorite handler (e.g. the read-only Library preview) → the
              radar bookmark is a no-op, so hide it rather than show a toggle
              that mutates only local UI state. */}
          {onToggleFavorite != null && (
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon-sm"
                    aria-label={bookmarkLabel}
                    disabled={isFavoriteLoading}
                    onClick={handleBookmarkClick}
                    className=" disabled:opacity-50 transition-transform active:scale-90"
                  >
                    <BookmarkIcon
                      className={`size-4 transition-[color,fill,transform] duration-base ease-out ${isBookmarked ? 'text-primary fill-primary scale-110' : 'text-foreground scale-100'}`}
                    />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="top">{bookmarkLabel}</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          )}

          <DropdownMenu
            onOpenChange={(open) => {
              if (open) menuInteractedRef.current = true;
              else setTimeout(() => (menuInteractedRef.current = false), 0);
            }}
          >
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon-sm"
                      aria-label={t('opportunity.opportunitiesCardV2.moreActions')}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <EllipsisVerticalIcon className="size-4" />
                    </Button>
                  </DropdownMenuTrigger>
                </TooltipTrigger>
                <TooltipContent side="top">
                  {t('opportunity.opportunitiesCardV2.moreActions')}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                disabled={!onOpenDetail}
                onSelect={() => onOpenDetail?.(item.opportunityId)}
              >
                {t('opportunity.opportunitiesCardV2.viewOpportunityDetail')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* ── Content ────────────────────────────────────────────────────── */}
      <div className="flex h-full flex-col justify-between gap-md px-lg pt-md pb-lg">
        <div className="flex items-start gap-md">
          {downloadUrl && (
            <div
              className="size-27.5 shrink-0 overflow-hidden rounded-lg bg-surface"
              data-testid="opportunity-image"
            >
              <img src={downloadUrl} alt="" className="size-full object-cover" />
            </div>
          )}
          <p
            data-testid="opportunity-title"
            className="line-clamp-4 min-w-0 flex-1 text-lg leading-[23px] font-medium tracking-tight text-foreground"
          >
            {item.opportunityTitle}
          </p>
        </div>

        <div className="flex flex-col gap-3">
          <div data-testid="opportunity-insight-type" className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground">
              {t('opportunity.opportunitiesCardV2.insightType')}
            </span>
            {item.insightType ? (
              <InsightTypeBadge type={item.insightType} />
            ) : (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Badge variant="secondary" roundness="full" size="sm" className="opacity-50">
                      {t('opportunity.opportunitiesCardV2.notDefined')}
                    </Badge>
                  </TooltipTrigger>
                  <TooltipContent side="top">
                    {t('opportunity.opportunitiesCardV2.ourAiCouldnTAccurately')}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
          <Separator className="bg-border-light" />
          <div className="flex items-start justify-between gap-4">
            <span className="text-xs text-muted-foreground shrink-0">
              {t('opportunity.opportunitiesCardV2.audience')}
            </span>
            {audienceTagList.length > 0 ? (
              <AudienceTags audiences={audienceTagList} maxVisibleTags={1} />
            ) : (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Badge variant="secondary" roundness="full" size="sm" className="opacity-50">
                      {t('opportunity.opportunitiesCardV2.noAudienceMatch')}
                    </Badge>
                  </TooltipTrigger>
                  <TooltipContent side="top">
                    {t('opportunity.opportunitiesCardV2.thisOpportunityDoesnTMatch')}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
